import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/*
  Max Customs: "spend $40, pick a free decal" reward.
  The progress bar itself is rendered by snippets/mc-free-decal.liquid. This file:
  - opens the "Pick your free decal" popup and puts the chosen decal in the cart
  - keeps the cart honest: one free decal max, and it comes out if the cart drops under the goal
*/

const SOURCE = 'mc-gift';
const SHOWN_KEY = 'mc-gift-shown';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
const fmt = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);

const el = document.getElementById('mc-gift');
if (el instanceof HTMLDialogElement) init(el);

/** @param {HTMLDialogElement} dialog */
function init(dialog) {
  const GOAL = Number(dialog.dataset.goal) || 4000;
  const HANDLE = dialog.dataset.giftHandle || '';
  const confirmBtn = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-mc-gift-confirm]'));
  const errorBox = /** @type {HTMLElement | null} */ (dialog.querySelector('.mc-offer__error'));

  /** @type {{ gifts: any[], sub: number, unlocked: boolean } | null} */
  let state = null;
  let busy = false;
  let queued = false;
  let lastPicked = 0;
  let promptTimer = 0;

  const cards = () => /** @type {HTMLElement[]} */ ([...dialog.querySelectorAll('[data-mc-gift-card]')]);
  const pillsOf = (card) => /** @type {HTMLElement[]} */ ([...card.querySelectorAll('[data-mc-gift-variant]')]);

  /* ---------- Cart helpers ---------- */

  async function getCart() {
    const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function analyze(cart) {
    const gifts = cart.items.filter((i) => i.handle === HANDLE);
    const sub = cart.items
      .filter((i) => i.handle !== HANDLE)
      .reduce((sum, i) => sum + Number(i.original_line_price || 0), 0);
    return { gifts, sub, unlocked: sub >= GOAL };
  }

  async function updateCart(updates) {
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const cart = await res.json();
    if (!res.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');
    // Tell the theme the cart changed so the drawer, cart page and cart icon re-render.
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count }));
    return cart;
  }

  /* ---------- Keep the cart honest ---------- */

  async function sync() {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;
    try {
      let cart = await getCart();
      let next = analyze(cart);
      /** @type {Record<string, number>} */
      const updates = {};

      if (next.gifts.length && !next.unlocked) {
        // Dropped under the goal: the free decal comes back out.
        next.gifts.forEach((g) => (updates[g.key] = 0));
      } else if (next.gifts.length > 1 || (next.gifts[0] && next.gifts[0].quantity > 1)) {
        // One free decal per order.
        const keep = next.gifts.find((g) => g.variant_id === lastPicked) || next.gifts[0];
        next.gifts.forEach((g) => (updates[g.key] = g === keep ? 1 : 0));
      }

      if (Object.keys(updates).length) {
        cart = await updateCart(updates);
        next = analyze(cart);
      }
      state = next;
      maybePrompt();
    } catch (err) {
      console.error('[mc-gift]', err);
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  }

  /* ---------- Popup ---------- */

  const cartIsVisible = () =>
    Boolean(document.querySelector('.cart-drawer__dialog[open]')) || Boolean(document.querySelector('.cart-page'));

  const canPrompt = () => {
    if (!state || !state.unlocked || state.gifts.length) return false;
    if (dialog.open || document.querySelector('#mc-offer[open]')) return false;
    if (!cartIsVisible()) return false;
    try {
      if (sessionStorage.getItem(SHOWN_KEY) === '1') return false;
    } catch (e) {}
    return true;
  };

  // The first time the cart reaches the goal while the customer is looking at it, show the picker once.
  function maybePrompt() {
    clearTimeout(promptTimer);
    if (!canPrompt()) return;
    promptTimer = window.setTimeout(() => {
      if (canPrompt()) openPicker();
    }, 700);
  }

  function select(card, pill) {
    cards().forEach((c) => {
      const on = c === card;
      c.classList.toggle('is-selected', on);
      c.querySelectorAll('[data-mc-gift-pick]').forEach((b) => b.setAttribute('aria-pressed', String(on)));
      pillsOf(c).forEach((p) => p.classList.toggle('is-active', on && p === pill));
    });
    updateFooter();
  }

  const selectedPill = () => dialog.querySelector('[data-mc-gift-card].is-selected [data-mc-gift-variant].is-active');

  function updateFooter() {
    if (!confirmBtn) return;
    const pill = selectedPill();
    confirmBtn.disabled = !pill;
    confirmBtn.classList.toggle('has-items', Boolean(pill));
    confirmBtn.textContent = pill ? `Add my free ${pill.getAttribute('data-mc-gift-name')}` : 'Pick a decal above';
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = !message;
  }

  function openPicker() {
    try {
      sessionStorage.setItem(SHOWN_KEY, '1');
    } catch (e) {}
    showError('');

    // Pre-select the decal that is already in the cart (when changing it).
    const current = state && state.gifts[0] ? String(state.gifts[0].variant_id) : '';
    const currentPill = current ? dialog.querySelector(`[data-mc-gift-variant="${current}"]`) : null;
    select(currentPill ? currentPill.closest('[data-mc-gift-card]') : null, currentPill);

    if (!dialog.open) dialog.showModal();
    dialog.querySelector('.mc-offer__grid')?.scrollTo({ top: 0 });
  }

  async function confirmPick() {
    const pill = selectedPill();
    if (!pill || !confirmBtn) return;
    const variantId = Number(pill.getAttribute('data-mc-gift-variant'));

    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Adding…';
    showError('');
    busy = true;
    try {
      const now = analyze(await getCart());
      if (!now.unlocked) {
        state = now;
        showError(`Add ${fmt(GOAL - now.sub)} more to your cart to unlock your free decal.`);
        updateFooter();
        return;
      }

      /** @type {Record<string, number>} */
      const updates = {};
      now.gifts.forEach((g) => {
        if (g.variant_id !== variantId) updates[g.key] = 0;
      });
      updates[String(variantId)] = 1;

      lastPicked = variantId;
      state = analyze(await updateCart(updates));
      dialog.close();
    } catch (err) {
      console.error('[mc-gift]', err);
      showError('Sorry, that one couldn’t be added. Try another decal.');
      updateFooter();
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  }

  dialog.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;

    // Tap on the dimmed backdrop closes the popup.
    if (t === dialog) {
      const r = dialog.getBoundingClientRect();
      const outside =
        event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
      if (outside) dialog.close();
      return;
    }

    if (t.closest('[data-mc-gift-close], [data-mc-gift-skip]')) {
      dialog.close();
      return;
    }

    const pill = t.closest('[data-mc-gift-variant]');
    if (pill) {
      select(pill.closest('[data-mc-gift-card]'), pill);
      return;
    }

    const pick = t.closest('[data-mc-gift-pick]');
    if (pick) {
      const card = /** @type {HTMLElement} */ (pick.closest('[data-mc-gift-card]'));
      select(card, card.querySelector('[data-mc-gift-variant].is-active') || pillsOf(card)[0]);
      return;
    }

    if (t.closest('[data-mc-gift-confirm]')) confirmPick();
  });

  // "Choose my free decal" / "Change decal" buttons on the progress bar
  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t || !t.closest('[data-mc-gift-open]')) return;
    event.preventDefault();
    clearTimeout(promptTimer);
    openPicker();
  });

  /* ---------- Triggers ---------- */

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const data = /** @type {any} */ (event).detail?.data || {};
    if (data.source === SOURCE || data.didError) return;
    sync();
  });

  // Cart drawer opened
  const drawer = document.querySelector('.cart-drawer__dialog');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.hasAttribute('open')) sync();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // Page load, and coming back from checkout with the back button
  window.addEventListener('pageshow', () => sync());
  if (document.readyState === 'complete') sync();
}
